import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "../lib/api";
import { InfoD } from "./InfoD";
import {
  Package, BarChart3, Stethoscope, AlertTriangle, ShoppingCart, KeyRound, Target,
  ArrowUpRight, ArrowDownRight, Minus, Loader2, Upload, TrendingDown, FileDown, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";

const eur = (v) => `${Number(v || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`;
const num = (v) => Number(v || 0).toLocaleString("es-ES");
const pct = (v) => `${Number(v || 0).toFixed(1)} %`;

function AlertasCaida({ alertas, comp }) {
  if (!alertas?.length) return null;
  const mismos = comp?.parcial || comp?.referencia_parcial?.length;
  return (
    <div className="card p-5 mb-6 border-red-200 bg-red-50/60" data-testid="alertas-caida">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <TrendingDown size={18} className="text-red-600" />
        <h3 className="font-display text-lg font-bold text-red-900">Alertas de caída</h3>
        <span className="text-xs text-red-700">empeoran más de un 10 % respecto al mes anterior</span>
      </div>
      <div className="text-xs text-red-900/80 mb-3" data-testid="alertas-periodo">
        Comparando <b>{comp?.ultimo}</b> con <b>{comp?.anterior}</b>.{" "}
        {mismos ? <span className="font-semibold text-emerald-700">Mismos días en los dos meses.</span>
          : <span>Los dos meses están completos.</span>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {alertas.map((a) => (
          <div key={a.indicador} className="bg-white rounded-lg border border-red-200 px-3 py-2 flex items-center justify-between gap-2" data-testid={`alerta-${a.indicador}`}>
            <div>
              <div className="text-sm font-semibold">{a.label}</div>
              <div className="text-xs text-slate-500 font-mono">{a.anterior} → {a.actual}</div>
            </div>
            <span className="text-sm font-bold text-red-600">{a.variacion > 0 ? "+" : ""}{a.variacion} %</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function InformeMensual() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/dashboard/report-settings").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  const toggle = async (v) => {
    setCfg((c) => ({ ...c, monthly_email: v }));
    try {
      await api.put("/dashboard/report-settings", { monthly_email: v });
      toast.success(v ? "Envío mensual activado" : "Envío mensual desactivado");
    } catch {
      setCfg((c) => ({ ...c, monthly_email: !v }));
      toast.error("No se pudo guardar");
    }
  };

  const descargar = async () => {
    setBusy(true);
    try {
      const r = await api.get("/dashboard/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "informe_mensual.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo generar el PDF");
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;
  return (
    <div className="card p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="informe-mensual">
      <div className="flex items-center gap-3 min-w-0">
        <Mail size={16} className="text-slate-500 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold">Informe mensual por email</div>
          <div className="text-xs text-slate-500 break-all">Se envía a {cfg.email} al subir un mes nuevo de ventas</div>
        </div>
        <Switch checked={cfg.monthly_email} onCheckedChange={toggle} className="shrink-0 data-[state=checked]:bg-emerald-600 data-[state=unchecked]:bg-slate-300" data-testid="informe-mensual-switch" />
      </div>
      <button onClick={descargar} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-slate-300 hover:bg-slate-100 transition-colors" data-testid="informe-pdf-download-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Descargar PDF
      </button>
    </div>
  );
}

function ResumenIA() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (regenerar = false) => {
    setBusy(true);
    api.get("/dashboard/ai-summary", { params: { regenerar }, timeout: 120000 })
      .then((r) => setD(r.data))
      .catch(() => toast.error("No se pudo generar el resumen con IA"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);
  if (!busy && !d?.texto) return null;
  return (
    <div className="card p-5 mb-6" data-testid="resumen-ia-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Resumen del mes · IA {d?.mes ? `· ${d.mes}` : ""}</div>
        <button onClick={() => load(true)} disabled={busy} className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="resumen-ia-regenerar">
          {busy ? <Loader2 size={12} className="animate-spin" /> : null} Regenerar
        </button>
      </div>
      {busy && !d?.texto ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Escribiendo el resumen…</div> : (
        <div className="text-sm text-slate-700 space-y-1.5" data-testid="resumen-ia-texto">
          {d.texto.split("\n").filter((p) => p.trim()).map((p, i) => (
            ["Lo mejor", "A vigilar", "Qué hacer este mes"].includes(p.trim())
              ? <div key={i} className="font-display font-bold text-slate-900 pt-1">{p.trim()}</div>
              : <p key={i}>{p}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function MargenLibre({ serie }) {
  if (!serie?.length) return null;
  const cur = serie[serie.length - 1];
  const prev = serie.length > 1 ? serie[serie.length - 2] : null;
  const diff = prev && cur.margen != null && prev.margen != null ? +(cur.margen - prev.margen).toFixed(1) : null;
  const data = serie.map((s) => ({ label: s.estudio, Margen: s.margen }));
  return (
    <div className="card p-5 mb-6 grid md:grid-cols-[260px_1fr] gap-6 items-center" data-testid="margen-libre-card">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Margen actual · venta libre</div>
        <div className="font-display text-4xl font-extrabold mt-1" data-testid="margen-libre-value">
          {cur.margen == null ? "—" : `${cur.margen.toLocaleString("es-ES")} %`}
        </div>
        <div className="text-xs text-slate-500 mt-1">{cur.estudio} · {cur.productos?.toLocaleString("es-ES")} productos de venta libre con stock</div>
        {diff != null && (
          <div className={`text-xs font-semibold mt-1 ${diff > 0 ? "text-emerald-600" : diff < 0 ? "text-red-600" : "text-slate-500"}`} data-testid="margen-libre-delta">
            {diff > 0 ? "▲ +" : diff < 0 ? "▼ " : "= "}{diff.toLocaleString("es-ES")} pp frente a {prev.estudio}
          </div>
        )}
      </div>
      {serie.length > 1 ? (
        <ResponsiveContainer width="100%" height={150}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} domain={["auto", "auto"]} allowDecimals={false} tickFormatter={(v) => `${Math.round(v)} %`} />
            <Tooltip formatter={(v) => `${v} %`} />
            <Line type="monotone" dataKey="Margen" stroke="#0f172a" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      ) : <div className="text-sm text-slate-500">La evolución aparecerá al subir el siguiente estudio de stock.</div>}
    </div>
  );
}

function Delta({ value, inverse = false, unit = "%" }) {
  if (value === null || value === undefined) return <span className="text-xs text-slate-400">—</span>;
  const good = inverse ? value < 0 : value > 0;
  const Icon = value === 0 ? Minus : good ? ArrowUpRight : ArrowDownRight;
  const color = value === 0 ? "text-slate-400" : good ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`text-xs font-semibold inline-flex items-center gap-0.5 ${color}`}>
      <Icon size={13} />{value > 0 ? "+" : ""}{value} {unit}
    </span>
  );
}

const CARDS = [
  { to: "/subidas/abcd", label: "Subidas", icon: Upload, key: "subidas" },
  { to: "/studies", label: "Estudios ABCD", icon: Package, key: "estudios" },
  { to: "/stock-parado/farmacia", label: "Stock parado", icon: AlertTriangle, key: "parado" },
  { to: "/ventas", label: "Ventas mensuales", icon: BarChart3, key: "ventas" },
  { to: "/empleados?tab=incentivos", label: "Incentivos", icon: Target, key: "incentivos" },
  { to: "/empleados?tab=consejo", label: "Consejo y patologías", icon: Stethoscope, key: "consejo" },
  { to: "/reaprovisionamientos", label: "Reaprovisionamientos", icon: ShoppingCart, key: "pedidos" },
  { to: "/admin/licencias", label: "Licencias", icon: KeyRound, key: "licencias", admin: true },
];

export const ResumenGeneral = ({ isAdmin }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/overview")
      .then((r) => setD(r.data))
      .catch(() => setD(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="card p-6 mb-8 text-slate-500 flex items-center gap-2" data-testid="resumen-loading"><Loader2 size={16} className="animate-spin" /> Cargando resumen…</div>;
  if (!d) return null;

  const todos = d.meses || [];
  const meses = todos.slice(-12);
  const ult = meses[meses.length - 1];
  const comp = d.comparativa;
  const resumen = {
    subidas: `${d.stock.estudios} estudio(s) · ${todos.length} mes(es)`,
    estudios: d.stock.productos ? `${num(d.stock.productos)} productos · ${eur(d.stock.valor_stock)}` : "Sin estudios",
    parado: d.stock.parados_n ? `D real ${num(d.stock.parados_real_n)} refs · ${eur(d.stock.parados_real_valor)} · todo el D ${eur(d.stock.parados_valor)}` : "—",
    ventas: ult ? `${eur(ult.facturacion)} · ${num(ult.operaciones)} operaciones` : "Sin meses subidos",
    incentivos: ult ? `Ticket medio ${eur(ult.ticket_medio)}` : "—",
    consejo: d.consejo.ratio_ultimo_mes != null ? `Recetas con consejo ${pct(d.consejo.ratio_ultimo_mes)}` : `${num(d.consejo.nomenclator)} productos`,
    pedidos: "Propuestas por laboratorio",
    licencias: d.licencias ? `${d.licencias.activas}/${d.licencias.total} activas` : "—",
  };

  const chart = meses.map((m) => ({
    mes: (m.label || m.mes || "").replace(" 20", " '"),
    Facturación: m.facturacion,
    "Venta libre": m.venta_libre,
    Parafarmacia: m.parafarmacia,
    "% parafarmacia": m.peso_parafarmacia,
    "Ticket medio": m.ticket_medio,
    "Ratio consejo": m.ratio_consejo,
    "% venta libre": m.peso_venta_libre,
    Operaciones: m.operaciones,
  }));

  return (
    <div className="mb-10" data-testid="resumen-general">
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">Resumen general</h2>
      <p className="text-slate-600 text-sm mb-4">Todo lo que tienes cargado y cómo evoluciona mes a mes.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {CARDS.filter((c) => !c.admin || isAdmin).map((c) => (
          <Link key={c.key} to={c.to} className="card p-4 hover:border-slate-400 transition-colors" data-testid={`resumen-card-${c.key}`}>
            <div className="flex items-center gap-2 mb-1">
              <c.icon size={15} className="text-slate-500" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{c.label}</span>
              {c.key === "parado" && <InfoD term="todos" />}
            </div>
            <div className="text-sm font-semibold leading-tight">{resumen[c.key]}</div>
          </Link>
        ))}
      </div>

      <InformeMensual />
      {meses.length > 0 && <ResumenIA />}
      <MargenLibre serie={d.margenes_libre} />

      {meses.length > 0 && (
        <>
          <AlertasCaida alertas={comp?.alertas} comp={comp} />
          <div className="card p-5 mb-6" data-testid="comparativa-indicadores">
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-display text-lg font-bold">Comparativa de indicadores</h3>
              <span className="text-xs text-slate-500">
                {comp?.anterior ? `${comp.anterior} → ${comp.ultimo}` : comp?.ultimo}
                {comp?.parcial && <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold" data-testid="comparativa-parcial">Periodo parcial: mismos días</span>}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="dense" data-testid="comparativa-table">
                <thead>
                  <tr>
                    <th>Indicador</th>
                    {meses.map((m) => <th key={m.import_id} className="text-right">{m.label}</th>)}
                    <th className="text-right">Variación</th>
                    {comp?.deltas_anio && <th className="text-right" data-testid="comparativa-col-anio">vs {comp.anio_anterior}</th>}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { k: "facturacion", label: "Facturación", fmt: eur },
                    { k: "operaciones", label: "Operaciones", fmt: num },
                    { k: "ticket_medio", label: "Ticket medio", fmt: eur },
                    { k: "venta_libre", label: "Venta libre", fmt: eur },
                    { k: "peso_venta_libre", label: "% venta libre", fmt: pct },
                    { k: "farmacia", label: "Farmacia", fmt: eur },
                    { k: "parafarmacia", label: "Parafarmacia", fmt: eur },
                    { k: "peso_parafarmacia", label: "% parafarmacia", fmt: pct },
                    { k: "ratio_consejo", label: "Recetas con consejo", fmt: pct },
                    { k: "uds_libre_por_receta", label: "Uds. libres por receta", fmt: (v) => Number(v || 0).toFixed(2) },
                    { k: "solo_receta", label: "Tickets solo receta", fmt: num },
                    { k: "mixtos", label: "Tickets mixtos", fmt: num },
                    { k: "solo_libre", label: "Tickets solo libre", fmt: num },
                  ].map((row) => (
                    <tr key={row.k} data-testid={`comparativa-row-${row.k}`}>
                      <td className="font-semibold">{row.label}</td>
                      {meses.map((m) => <td key={m.import_id} className="text-right font-mono text-xs">{row.fmt(m[row.k])}</td>)}
                      <td className="text-right"><Delta value={comp?.deltas?.[row.k]} inverse={row.k === "solo_receta"} unit={row.fmt === pct ? "pp" : "%"} /></td>
                      {comp?.deltas_anio && <td className="text-right"><Delta value={comp.deltas_anio[row.k]} inverse={row.k === "solo_receta"} unit={row.fmt === pct ? "pp" : "%"} /></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card p-5" data-testid="chart-facturacion">
              <h3 className="font-display text-lg font-bold mb-3">Evolución de la facturación</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chart} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Facturación" fill="#0f172a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Venta libre" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Parafarmacia" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="card p-5" data-testid="chart-consejo">
              <h3 className="font-display text-lg font-bold mb-3">Ticket medio y ratio de consejo</h3>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={chart} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="l" type="monotone" dataKey="Ticket medio" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="r" type="monotone" dataKey="Ratio consejo" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="r" type="monotone" dataKey="% venta libre" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="r" type="monotone" dataKey="% parafarmacia" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}

      {meses.length === 0 && (
        <div className="card p-6 text-sm text-slate-600" data-testid="resumen-sin-meses">
          Sube tu primer mes de ventas en <Link to="/subidas/ventas" className="underline font-semibold">Subidas → Ventas mes a mes</Link> y aquí aparecerá la evolución de los indicadores.
        </div>
      )}
    </div>
  );
};
